// START IMPORTS
// Refresh lib imports
import googleTagManager, {
  GoogleTagManagerConfig,
} from '@analytics/google-tag-manager';
import { initialize } from '@ltv-apm-modules/react';
import { version, CookiesStorage } from '@ltvco/refresh-lib';
import {
  AgentChatProvider,
  CanAccessProvider,
  SubscriptionAccessConfigProvider,
} from '@ltvco/refresh-lib/ctx';
import { SearchPreferencesProvider } from '@ltvco/refresh-lib/experiments/RFRSH-4625';
import {
  FamilyAlbum,
  AIFamilyPhotosPage,
  FamilyInsightsPage,
  FamilyMapPage,
} from '@ltvco/refresh-lib/fmt';
import {
  DevErrorFallback,
  ErrorBoundary,
  styled,
} from '@ltvco/refresh-lib/theme';
import { ErrorRequestData, ResponseError } from '@ltvco/refresh-lib/utils';
import {
  closeSnackbar,
  ContactOptionsModal,
  ForgotLoginModal,
  invalidateSession,
  isUserLoggedIn,
  MagicLinkModal,
  ResetPasswordRequestForm,
  SessionProvider,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  ImportList,
  CancellationReasonsModal,
  CancelUserExperienceModal,
  ConfirmCancelModal,
  SearchFormProvider,
  Footer,
  BannerMigrationCta,
  ComplianceRecomplyPage,
  ComplianceFormPage,
  CompliancePermittedPage,
  ComplianceProhibitedPage,
  SalesLeadModal,
  SelfSearchingContainer,
  NonCompliancePage,
  ObituarySearchPage,
  EmailSearchPage,
  CommunityBuzzHome,
  CommunityBuzzMyPosts,
  CommunityBuzzMyComments,
  HomesForSalePage,
  MonitoringCenter,
  ShortTermRentalsPage,
  TrustPilotModal,
} from '@ltvco/refresh-lib/v2';
import Analytics from 'analytics';
import { useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  Navigate,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

import 'App.css';
import { constants } from 'appConstants';
import { AgentChatSurface } from 'components/AgentChat/AgentChatSurface';
import { AppContainer } from 'components/AppContainer';
import { BaseNumerologyQueryKeys } from 'components/Astrology/interfaces';
import { DeepSearchEntry } from 'components/DeepSearch/DeepSearchEntry';
import { EmailVerification } from 'components/EmailVerification';
import { Growthbook } from 'components/Growthbook';
import { LibContextWrapper } from 'components/LibContextWrapper';
import QueryWrapper from 'components/queryWrapper/QueryWrapper';
import { Recycling } from 'components/recycling/Recycling';
import { DeviceFraudReport } from 'components/reports';
import { SALES_LEAD_MODAL_CONFIG } from 'components/salesLeadModalConfig';
import { SeatRestrictedRoute } from 'components/SeatRestrictedRoute';
import { TrustpilotLoginPromptGate } from 'components/TrustpilotLoginPromptGate';
import { UpgradeLimitModalGate } from 'components/UpgradeLimitModalGate';
import {
  FraudWithSearchSuccess,
  HeaderParentWithSearchSuccess,
  ReportWithSearchSuccess,
  SearchSuccessRedirectProvider,
  TosModalLegalErrorWithSearchSuccess,
} from 'contexts/SearchSuccessRedirectContext';
import {
  DashboardParent,
  Login,
  RecordPage,
  AuthenticatedRoute,
  UnclaimedMoney,
  DarkWebScan,
  Billing,
  ListsItem,
  SearchPersonResultsContainerv2,
  SearchListingResults,
  ResetPassword,
  Reactivation,
  PublicRoute,
  NotFound,
  ErrorPage,
  UpgradePlanPage,
  ReportVelocityCaptcha,
  NeighborhoodReportSection,
  RewardsPage,
  MyGarage,
  SearchObituaryResults,
  Referrals,
  FamilyTreePage,
  PersonalityTraitsPage,
  AstrologyPage,
  RecentReports,
  DashboardPage,
  NameHistorySearch,
  RecallReport,
  CourtCaseReport,
  FeatureGatedRoute,
  IdentityHub,
  MemberProfile,
  LegalIntelligenceLanding,
  ApiPage,
  ContactEnrichment,
  FamilyAndAncestryDashboard,
  LeadsSearchPage,
  LeadScoreResultsPage,
  PropertyReportMapPage,
} from 'routes';
import {
  CaseDetails,
  CourtCasesParentV2,
  OpinionDetails,
  OralArgumentDetails,
} from 'routes/Dashboard/CourtCases';
import { CourtCasesBaseRedirect } from 'routes/Dashboard/CourtCases/v2/CourtCasesBaseRedirect';
import { CreativeWorksSearchDetails } from 'routes/Dashboard/CourtCases/v2/CreativeWorksSearch/CreativeWorksSearchDetails';
import { DigitalFootprint } from 'routes/Dashboard/DigitalFootprint/DigitalFootprint';
import { IntellectualPropertyPage } from 'routes/Dashboard/IntellectualProperty/IntellectualPropertyPage';
import { OwnerSearchParent } from 'routes/Dashboard/OwnerSearch/OwnerSearchParent';
import { PlanMembersPage } from 'routes/Dashboard/PlanMembers';
import { generateFooterLinks } from 'utils/footerLinks/footerLinks';
import { goToLogin } from 'utils/goToLogin';

import packageInfo from '../package.json';
// END IMPORTS

// START INTERFACES_AND_TYPES
declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    env: Record<string, string>;
  }
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
let otel: any = null;

if (
  import.meta.env.VITE_TARGET_ENV === 'production' ||
  import.meta.env.VITE_TARGET_ENV === 'staging'
) {
  otel = initialize({
    name: 'refresh-beenverified',
    url: import.meta.env.VITE_APM_URL,
    apikey: import.meta.env.VITE_APM_KEY,
  });
}
// END CONSTANTS

// START STYLED_COMPONENTS
const HeaderContainer = styled('div')`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 20;
`;

const MigrationBannerContainer = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const LibVersion = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 0 0 0`,
  fontSize: theme.typography.fontSize * 0.75,
}));
// END STYLED_COMPONENTS

// START HELPER_FUNCTIONS
function removeSensitiveLocalStorageData() {
  localStorage.removeItem('astrology_user');
  localStorage.removeItem(BaseNumerologyQueryKeys.lifepath);
  localStorage.removeItem(BaseNumerologyQueryKeys.personality);
}

function removeFamilyTreeLocalStorageData() {
  localStorage.removeItem('nodeRemovalApproved');
  localStorage.removeItem('reportIdsToFilterByType');
  localStorage.removeItem('bulkAddSubject');
  localStorage.removeItem('bulkAddSubjectReportInTree');
  localStorage.removeItem('bulkAddSubjectFamily');
  localStorage.removeItem('familyTreeRelativeToAdd');
  localStorage.removeItem('skipAutoTreeBuilder');
}
// END HELPER_FUNCTIONS

function App() {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const [isCoreLoginRedirect] = useQueryParam(
    'is_core_login_redirect',
    StringParam
  );
  const [loginType] = useQueryParam('login_type', StringParam);
  const loginPageContainerRef = useRef(null);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isAuthenticated, setIsAuthenticated] = useState(isUserLoggedIn());
  const [isContactOptionsModalOpen, setIsContactOptionsModalOpen] =
    useState(false);
  const [isUpgradeLeadModalOpen, setIsUpgradeLeadModalOpen] = useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);
  const [isCancellationReasonsModalOpen, setIsCancellationReasonsModalOpen] =
    useState(false);
  const [, setIsDownsellOfferModalOpen] = useState(false);
  const [isUserExperienceSurveyModalOpen, setIsUserExperienceSurveyModalOpen] =
    useState(false);
  const [isTrustPilotModalOpen, setIsTrustPilotModalOpen] = useState(false);
  const [isSalesLeadModalOpen, setIsSalesLeadModalOpen] = useState(false);
  const [isInSingleReportVariation, setIsInSingleReportVariation] =
    useState(false);
  const [isInInternationalLanguageTest, setIsInInternationalLanguageTest] =
    useState(false);
  const [isInInternationalVinTest, setIsInInternationalVinTest] =
    useState(false);
  const [, setIsInListV2Variation] = useState(false);
  const [
    isInReactivationRedesignVariation,
    setIsInReactivationRedesignVariation,
  ] = useState(false);
  const [showCheaperPlanLink, setShowCheaperPlanLink] = useState(false);
  const [tosState, setTosState] = useState<{
    modalOpen: boolean;
    requestData: ErrorRequestData['body'] | null;
  }>({
    modalOpen: false,
    requestData: null,
  });
  const [isInOwnerSearchVariation, setIsInOwnerSearchVariation] =
    useState(false);
  const [
    isInAstrologyStandaloneVariation,
    setIsInAstrologyStandaloneVariation,
  ] = useState(false);
  const [, setIsSuccessModaVisible] = useState<boolean>(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const analytics = Analytics({
    app: 'refresh-beenverified',
    debug: true,
    plugins: [
      googleTagManager({
        containerId: 'GTM-HV76',
        dataLayer: (window as any)?.dataLayer ?? [],
      } as GoogleTagManagerConfig),
    ],
  });
  const storage = new CookiesStorage();
  const isDeepSearchRoute = location.pathname.includes('deepsearch');
  const showFooter =
    !location.pathname.includes('self-searching') && !isDeepSearchRoute;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (
      isCoreLoginRedirect === 'true' &&
      !sessionStorage.getItem('has_tracked_core_login_redirect')
    ) {
      analytics.track('site_event', {
        event_name: 'login_attempt',
        type: loginType || 'web',
        success: true,
      });

      if (loginType?.includes('sso')) {
        analytics.track('site_event', {
          event_name: 'sso_success',
          type: true,
        });
      }

      sessionStorage.setItem(
        'has_tracked_core_login_redirect',
        isCoreLoginRedirect
      );
    }
  }, [isCoreLoginRedirect]);
  // END EFFECTS

  // START EVENT_HANDLERS
  const changeIsInInternationalLanguageTest = (variation: boolean) => {
    setIsInInternationalLanguageTest(variation);
  };

  const changeIsInInternationalVinTest = (variation: boolean) => {
    setIsInInternationalVinTest(variation);
  };

  const changeIsInSingleReportVariation = (variation: boolean) => {
    setIsInSingleReportVariation(variation);
  };

  const changeIsInOwnerSearchVariation = (variation: boolean) => {
    setIsInOwnerSearchVariation(variation);
  };

  const changeIsInAstrologyStandaloneVariation = (variation: boolean) => {
    setIsInAstrologyStandaloneVariation(variation);
  };

  const changeIsInListV2Variation = (variation: boolean) => {
    setIsInListV2Variation(variation);
  };

  const changeIsInReactivationRedesignVariation = (variation: boolean) => {
    setIsInReactivationRedesignVariation(variation);
  };

  async function handleLogoutClick(ga4Ttracker?: () => void) {
    await invalidateSession();
    setIsAuthenticated(isUserLoggedIn());
    closeSnackbar();
    removeSensitiveLocalStorageData();
    removeFamilyTreeLocalStorageData();
    storage.setValue('partialLocView', 'false');
    localStorage.removeItem('upgradeLimitModalShown');
    goToLogin();
    if (ga4Ttracker) {
      ga4Ttracker();
    }
  }

  function isResponseError(error: any): error is ResponseError {
    return error.response !== undefined;
  }

  function logError(
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) {
    const isProdEnv = import.meta.env.VITE_TARGET_ENV === 'production';
    if (!isProdEnv || !otel) {
      return;
    }

    const { account } = session;
    const { api } = otel;
    if (api.isOTELInitialized()) {
      if (account) {
        api.setUser({
          id: account?.account.user_info.user_code,
          email: account?.account.user_info.email,
        });
      }
      let errorMsg = `${context}: ${error.message}, Cause: ${error.cause}`;
      if (isResponseError(error)) {
        errorMsg = `${errorMsg}, Status: ${
          error.response.status
        }, Data: ${JSON.stringify(error.response)}`;
      }
      if (url) {
        errorMsg += `, URL: ${url}`;
      }
      api.pushError(new Error(errorMsg));
    }
  }

  function trackEventGA4(eventToTrack: object, eventName?: string) {
    const targetEnv = import.meta.env.VITE_TARGET_ENV;
    if (targetEnv === 'dev') {
      return;
    }
    analytics?.track(eventName || 'site_event', eventToTrack);
  }

  function trackEvent(category: string, action: string, label?: string) {
    analytics.track('ua_event', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
    });
  }

  const onLoginSuccess = (
    isInInternationalLanguageTest: boolean,
    isInInternationalVinTest: boolean
  ) => {
    let next = '/';

    if (location.state && location.state.next) {
      next = location.state.next;
    }

    if (isInInternationalLanguageTest || isInInternationalVinTest) {
      isInInternationalVinTest
        ? navigate('/dashboard/reports')
        : navigate('/dashboard/language');
    } else {
      navigate(
        next === '/' ||
          (next.startsWith('/report') && !next.includes('permalink'))
          ? '/dashboard'
          : next
      );
    }

    setIsAuthenticated(isUserLoggedIn());
  };

  const contactUsHandler = () => {
    setIsContactOptionsModalOpen(true);
  };

  const requestApiAccessHandler = (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    setIsSalesLeadModalOpen(true);
  };

  const needMoreReportsHandler = (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    setIsUpgradeLeadModalOpen(true);
  };

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const handleCancellationModalClose = (reason: string | undefined) => {
    setIsCancellationReasonsModalOpen(false);
    if (reason === 'Too expensive') {
      setIsDownsellOfferModalOpen(true);
    } else if (reason === 'Other' || reason === 'No longer needed') {
      setIsUserExperienceSurveyModalOpen(true);
    }
  };

  const handleCloseUserExperienceSurveyModal = (
    rating: string | null | undefined
  ) => {
    setIsUserExperienceSurveyModalOpen(false);
    if (rating && parseInt(rating) >= 8) {
      setIsTrustPilotModalOpen(true);
    }
  };

  const openConfirmCancelModal = (downsellTest?: boolean) => {
    setIsConfirmCancelModalOpen(true);
    setShowCheaperPlanLink(downsellTest || false);
  };

  const onCancelConfirmCancelModal = () => {
    setIsConfirmCancelModalOpen(false);
    setIsCancellationReasonsModalOpen(true);
  };
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <ErrorBoundary
      redirectFunction={() => {
        navigate('/error');
      }}
      fallback={
        import.meta.env.DEV
          ? (error) => <DevErrorFallback error={error} />
          : undefined
      }
      location={location}
      logFunction={logError as any}
      trackEventFunction={trackEventGA4}
    >
      <QueryWrapper
        handleLogout={handleLogoutClick}
        trackEventGA4={trackEventGA4}
        setTosState={setTosState}
      >
        <SessionProvider isAuthenticated={isAuthenticated}>
          <SubscriptionAccessConfigProvider>
            <CanAccessProvider>
              <Growthbook>
                <SearchSuccessRedirectProvider>
                  <LibContextWrapper
                    trackEventGA4={trackEventGA4}
                    logError={logError}
                    trackEvent={trackEvent}
                    isInInternationalLanguageTest={
                      isInInternationalLanguageTest
                    }
                    isInInternationalVinTest={isInInternationalVinTest}
                    isInOwnerSearchVariation={isInOwnerSearchVariation}
                    isInSingleReportVariation={isInSingleReportVariation}
                    isInAstrologyStandaloneVariation={
                      isInAstrologyStandaloneVariation
                    }
                    handleLogoutClick={handleLogoutClick}
                  >
                    <EmailVerification>
                      <SearchPreferencesProvider>
                        <SearchFormProvider>
                          <AgentChatProvider>
                            <div className="app">
                              {!isDeepSearchRoute && (
                                <HeaderContainer>
                                  <MigrationBannerContainer>
                                    <BannerMigrationCta />
                                  </MigrationBannerContainer>
                                  <HeaderParentWithSearchSuccess
                                    changeSingleReportVariation={
                                      changeIsInSingleReportVariation
                                    }
                                    changeOwnerSearchVariation={
                                      changeIsInOwnerSearchVariation
                                    }
                                    changeInternationalLanguageTest={
                                      changeIsInInternationalLanguageTest
                                    }
                                    changeInternationalVinTest={
                                      changeIsInInternationalVinTest
                                    }
                                    changeIsInAstrologyStandaloneVariation={
                                      changeIsInAstrologyStandaloneVariation
                                    }
                                    changeListV2Variation={
                                      changeIsInListV2Variation
                                    }
                                  />
                                </HeaderContainer>
                              )}
                              <AppContainer>
                                <Routes>
                                  <Route element={<NotFound />} path={'*'} />
                                  <Route
                                    element={<ErrorPage />}
                                    path={'/error'}
                                  />
                                  <Route
                                    index
                                    element={<Navigate to={`/dashboard`} />}
                                  />
                                  <Route
                                    path={'/family-tree'}
                                    element={
                                      <AuthenticatedRoute>
                                        <FamilyTreePage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/self-searching`}
                                    element={
                                      <AuthenticatedRoute>
                                        <SelfSearchingContainer />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/login`}
                                    element={
                                      <PublicRoute>
                                        <Login
                                          onLoginSuccess={() =>
                                            onLoginSuccess(
                                              isInInternationalLanguageTest,
                                              isInInternationalVinTest
                                            )
                                          }
                                          ref={loginPageContainerRef}
                                        />
                                      </PublicRoute>
                                    }
                                  >
                                    <Route
                                      path="reset-password-request"
                                      element={
                                        <ResetPasswordRequestForm
                                          containerRef={loginPageContainerRef}
                                          open={false}
                                        />
                                      }
                                    />
                                    <Route
                                      path="magic-link"
                                      element={
                                        <MagicLinkModal
                                          containerRef={loginPageContainerRef}
                                          open={false}
                                        />
                                      }
                                    />
                                    <Route
                                      path="forgot-email"
                                      element={
                                        <ForgotLoginModal
                                          containerRef={loginPageContainerRef}
                                          open={false}
                                        />
                                      }
                                    />
                                  </Route>
                                  <Route
                                    path={`/reset-password`}
                                    element={
                                      <PublicRoute>
                                        <ResetPassword />
                                      </PublicRoute>
                                    }
                                  />
                                  <Route
                                    path={`dashboard`}
                                    element={
                                      <AuthenticatedRoute>
                                        <DashboardParent
                                          contactUsHandler={contactUsHandler}
                                          handleLogout={handleLogoutClick}
                                          openConfirmCancelModal={
                                            openConfirmCancelModal
                                          }
                                        />
                                      </AuthenticatedRoute>
                                    }
                                  >
                                    <Route
                                      path={`/dashboard/monitoring`}
                                      element={
                                        <AuthenticatedRoute>
                                          <MonitoringCenter />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      index
                                      element={
                                        <AuthenticatedRoute>
                                          <DashboardPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/reports`}
                                      element={
                                        <AuthenticatedRoute>
                                          <RecentReports />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/personality-traits`}
                                      element={
                                        <AuthenticatedRoute>
                                          <PersonalityTraitsPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    {isInAstrologyStandaloneVariation && (
                                      <Route
                                        path={'/dashboard/astrology'}
                                        element={
                                          <AuthenticatedRoute>
                                            <AstrologyPage />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                    )}
                                    <Route
                                      path={'/dashboard/community-buzz'}
                                      element={
                                        <AuthenticatedRoute>
                                          <CommunityBuzzHome />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={
                                        '/dashboard/community-buzz/my-posts'
                                      }
                                      element={
                                        <AuthenticatedRoute>
                                          <CommunityBuzzMyPosts />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={
                                        '/dashboard/community-buzz/my-comments'
                                      }
                                      element={
                                        <AuthenticatedRoute>
                                          <CommunityBuzzMyComments />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/lists/:listName`}
                                      element={
                                        <AuthenticatedRoute>
                                          <ListsItem />
                                        </AuthenticatedRoute>
                                      }
                                    />

                                    <Route
                                      path={`/dashboard/family-insights`}
                                      element={
                                        <AuthenticatedRoute>
                                          <FamilyInsightsPage />
                                        </AuthenticatedRoute>
                                      }
                                    />

                                    <Route
                                      path={`/dashboard/family-ancestry`}
                                      element={
                                        <AuthenticatedRoute>
                                          <FamilyAndAncestryDashboard />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/family-album`}
                                      element={
                                        <AuthenticatedRoute>
                                          <FamilyAlbum />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/ai-family-photos`}
                                      element={
                                        <AuthenticatedRoute>
                                          <AIFamilyPhotosPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/rewards`}
                                      element={
                                        <AuthenticatedRoute>
                                          <RewardsPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/dark-web`}
                                      element={
                                        <AuthenticatedRoute>
                                          <DarkWebScan />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/id-monitor`}
                                      element={
                                        <Navigate to="/dashboard" replace />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/id-monitor/*`}
                                      element={
                                        <Navigate to="/dashboard" replace />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/personal-reports`}
                                      element={
                                        <Navigate to="/dashboard" replace />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/personal-reports/*`}
                                      element={
                                        <Navigate to="/dashboard" replace />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/notifications`}
                                      element={
                                        <Navigate
                                          to="/dashboard/monitoring?tab=notifications"
                                          replace
                                        />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/lists`}
                                      element={
                                        <Navigate
                                          to="/dashboard/monitoring?tab=myLists"
                                          replace
                                        />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/lead-score`}
                                      element={
                                        <AuthenticatedRoute>
                                          <LeadsSearchPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/lead-score/report`}
                                      element={
                                        <AuthenticatedRoute>
                                          <LeadScoreResultsPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/my-saved-searches`}
                                      element={
                                        <Navigate
                                          to="/dashboard/monitoring?tab=savedSearches"
                                          replace
                                        />
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/identity-hub`}
                                      element={
                                        <AuthenticatedRoute>
                                          <IdentityHub />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/digital-footprint`}
                                      element={
                                        <AuthenticatedRoute>
                                          <DigitalFootprint />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'/dashboard/my-garage'}
                                      element={
                                        <AuthenticatedRoute>
                                          <MyGarage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'/dashboard/obituary'}
                                      element={
                                        <AuthenticatedRoute>
                                          <ObituarySearchPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'/dashboard/name-history'}
                                      element={
                                        <AuthenticatedRoute>
                                          <NameHistorySearch />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/contact-enrichment`}
                                      element={
                                        <AuthenticatedRoute>
                                          <ContactEnrichment />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/import-list`}
                                      element={
                                        <AuthenticatedRoute>
                                          <ImportList title="Bulk Property Search" />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'neighborhood-report'}
                                      element={
                                        <AuthenticatedRoute>
                                          <NeighborhoodReportSection />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'unclaimed-money'}
                                      element={
                                        <AuthenticatedRoute>
                                          <FeatureGatedRoute featureKey="unclaimed_money">
                                            <UnclaimedMoney />
                                          </FeatureGatedRoute>
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`/dashboard/email-search`}
                                      element={
                                        <AuthenticatedRoute>
                                          <FeatureGatedRoute
                                            featureKey={'email'}
                                          >
                                            <EmailSearchPage />
                                          </FeatureGatedRoute>
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'/dashboard/legal-intelligence'}
                                      element={
                                        <AuthenticatedRoute>
                                          <LegalIntelligenceLanding />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'/dashboard/creative-works-search'}
                                      element={
                                        <AuthenticatedRoute>
                                          <CourtCasesParentV2 />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={
                                        '/dashboard/creative-works-search/:id'
                                      }
                                      element={
                                        <AuthenticatedRoute>
                                          <CreativeWorksSearchDetails />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route path={'court-cases'}>
                                      <Route
                                        index
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCasesBaseRedirect />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={
                                          'class-action-lawsuits/report/:docketId'
                                        }
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCaseReport />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'class-action-lawsuits'}
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCasesParentV2 />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'cases'}
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCasesParentV2 />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'opinions'}
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCasesParentV2 />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'oral-arguments'}
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCasesParentV2 />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'recalls'}
                                        element={
                                          <AuthenticatedRoute>
                                            <CourtCasesParentV2 />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'recalls/report'}
                                        element={
                                          <AuthenticatedRoute>
                                            <RecallReport />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'opinions/:id'}
                                        element={
                                          <AuthenticatedRoute>
                                            <OpinionDetails />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'cases/:id'}
                                        element={
                                          <AuthenticatedRoute>
                                            <CaseDetails />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                      <Route
                                        path={'oral-arguments/:id'}
                                        element={
                                          <AuthenticatedRoute>
                                            <OralArgumentDetails />
                                          </AuthenticatedRoute>
                                        }
                                      />
                                    </Route>
                                    <Route
                                      path={'intellectual-property'}
                                      element={
                                        <AuthenticatedRoute>
                                          <IntellectualPropertyPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={'patent-search'}
                                      element={
                                        <Navigate
                                          to="/dashboard/intellectual-property"
                                          replace
                                        />
                                      }
                                    />
                                    <Route
                                      path={`billing`}
                                      element={
                                        <AuthenticatedRoute>
                                          <SeatRestrictedRoute>
                                            <Billing
                                              contactUsHandler={
                                                contactUsHandler
                                              }
                                              handleLogout={handleLogoutClick}
                                              onCancelConfirmCancelModal={
                                                onCancelConfirmCancelModal
                                              }
                                            />
                                          </SeatRestrictedRoute>
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path="plan-members"
                                      element={
                                        <AuthenticatedRoute>
                                          <PlanMembersPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path={`referrals`}
                                      element={
                                        <AuthenticatedRoute>
                                          <Referrals />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path="fraud"
                                      element={
                                        <AuthenticatedRoute>
                                          <FraudWithSearchSuccess />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                    <Route
                                      path="api"
                                      element={
                                        <AuthenticatedRoute>
                                          <ApiPage />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                  </Route>
                                  <Route
                                    path={`/member-profile/:member_id`}
                                    element={
                                      <AuthenticatedRoute>
                                        <MemberProfile />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/collection/:collection_id/record/:record_id`}
                                    element={
                                      <AuthenticatedRoute>
                                        <RecordPage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/family-map`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FamilyMapPage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path="/report/device-fraud"
                                    element={
                                      <AuthenticatedRoute>
                                        <DeviceFraudReport />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path="/report/property/map"
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute featureKey="property">
                                          <PropertyReportMapPage />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/report/:reportType`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          reportTypeToFeature={true}
                                        >
                                          <ReportWithSearchSuccess />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={'/deepsearch/*'}
                                    element={
                                      <AuthenticatedRoute>
                                        <DeepSearchEntry />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/search/person`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          featureKey={'person'}
                                        >
                                          <SearchPersonResultsContainerv2 />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/search/v2`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute featureKey="person">
                                          <SearchPersonResultsContainerv2 />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/search/listing`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          featureKey={'property'}
                                        >
                                          <SearchListingResults />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path="/homes-for-sale"
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          featureKey={'property'}
                                        >
                                          <HomesForSalePage />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path="/short-term-rental"
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          featureKey={'property'}
                                        >
                                          <ShortTermRentalsPage />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/search/obituary`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          featureKey={'obituary'}
                                        >
                                          <SearchObituaryResults />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/search/contact`}
                                    element={
                                      <AuthenticatedRoute>
                                        <FeatureGatedRoute
                                          featureKey={'contact'}
                                        >
                                          <SearchPersonResultsContainerv2 />
                                        </FeatureGatedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  {isInOwnerSearchVariation && (
                                    <Route
                                      path={`/search/owner`}
                                      element={
                                        <AuthenticatedRoute>
                                          <OwnerSearchParent />
                                        </AuthenticatedRoute>
                                      }
                                    />
                                  )}
                                  <Route
                                    path={`/noncompliance`}
                                    element={
                                      <AuthenticatedRoute>
                                        <NonCompliancePage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/compliance-form`}
                                    element={
                                      <AuthenticatedRoute>
                                        <ComplianceFormPage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/compliance-permitted`}
                                    element={
                                      <AuthenticatedRoute>
                                        <CompliancePermittedPage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/compliance-prohibited`}
                                    element={
                                      <AuthenticatedRoute>
                                        <ComplianceProhibitedPage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={`/compliance-recomply`}
                                    element={
                                      <AuthenticatedRoute>
                                        <ComplianceRecomplyPage />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={'/reactivation'}
                                    element={
                                      <AuthenticatedRoute>
                                        <Reactivation
                                          changeIsInReactivationRedesignVariation={
                                            changeIsInReactivationRedesignVariation
                                          }
                                        />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    index={true}
                                    path={'/upgrade/plan'}
                                    element={
                                      <AuthenticatedRoute>
                                        <SeatRestrictedRoute>
                                          <UpgradePlanPage
                                            contactUsHandler={contactUsHandler}
                                          />
                                        </SeatRestrictedRoute>
                                      </AuthenticatedRoute>
                                    }
                                  />
                                  <Route
                                    path={'/captcha'}
                                    element={
                                      <AuthenticatedRoute>
                                        <ReportVelocityCaptcha />
                                      </AuthenticatedRoute>
                                    }
                                  />
                                </Routes>
                                <Recycling isAuthenticated={isAuthenticated} />
                              </AppContainer>
                              <ContactOptionsModal
                                isOpen={isContactOptionsModalOpen}
                                setIsOpen={setIsContactOptionsModalOpen}
                                openConfirmCancelModal={openConfirmCancelModal}
                                onCustomPlanRequest={() =>
                                  setIsUpgradeLeadModalOpen(true)
                                }
                                plansWithout5ReportBlocker={
                                  constants.data.plansWithout5ReportBlocker
                                }
                                validReportCountThreshold={
                                  constants.data.validReportCountThreshold
                                }
                                blockUpgradeWindowThreshold={
                                  constants.data.blockUpgradeWindowThreshold
                                }
                              />
                              <SalesLeadModal
                                open={isUpgradeLeadModalOpen}
                                onClose={() => setIsUpgradeLeadModalOpen(false)}
                                variant="upgrade"
                                config={SALES_LEAD_MODAL_CONFIG}
                              />
                              <ConfirmCancelModal
                                isOpen={isConfirmCancelModalOpen}
                                onClose={() =>
                                  setIsConfirmCancelModalOpen(false)
                                }
                                onCancel={() => {
                                  setIsConfirmCancelModalOpen(false);
                                  setIsCancellationReasonsModalOpen(true);
                                }}
                                openSuccessModal={openSuccessModal}
                                showDownsell={true}
                                showCheaperPlanLink={showCheaperPlanLink}
                              />
                              <CancellationReasonsModal
                                isOpen={isCancellationReasonsModalOpen}
                                onClose={(reason) => {
                                  handleCancellationModalClose(reason);
                                }}
                              />
                              <TosModalLegalErrorWithSearchSuccess
                                tosState={tosState}
                                setTosState={setTosState}
                              />
                              {isUserExperienceSurveyModalOpen && (
                                <CancelUserExperienceModal
                                  onClose={(rating) =>
                                    handleCloseUserExperienceSurveyModal(rating)
                                  }
                                />
                              )}
                              <TrustPilotModal
                                isOpen={isTrustPilotModalOpen}
                                onClose={() => setIsTrustPilotModalOpen(false)}
                              />
                              {isAuthenticated && <AgentChatSurface />}
                              <UpgradeLimitModalGate />
                              <TrustpilotLoginPromptGate />
                              <SalesLeadModal
                                open={isSalesLeadModalOpen}
                                onClose={() => setIsSalesLeadModalOpen(false)}
                                config={SALES_LEAD_MODAL_CONFIG}
                              />
                              {showFooter && (
                                <Footer
                                  footerLinks={generateFooterLinks({
                                    contactUsHandler,
                                    requestApiAccessHandler,
                                    needMoreReportsHandler,
                                    isInternationalTestUser:
                                      isInInternationalLanguageTest ||
                                      isInInternationalVinTest,
                                  })}
                                  libVersion={
                                    <LibVersion>
                                      {import.meta.env.VITE_TARGET_ENV ===
                                      'production' ? (
                                        <code>{version}</code>
                                      ) : (
                                        <code>
                                          {
                                            packageInfo.dependencies[
                                              '@ltvco/refresh-lib'
                                            ]
                                          }
                                        </code>
                                      )}
                                    </LibVersion>
                                  }
                                  hasMobileApps
                                  userInReactivationRedesignTest={
                                    isInReactivationRedesignVariation
                                  }
                                />
                              )}
                            </div>
                          </AgentChatProvider>
                        </SearchFormProvider>
                      </SearchPreferencesProvider>
                    </EmailVerification>
                  </LibContextWrapper>
                </SearchSuccessRedirectProvider>
              </Growthbook>
            </CanAccessProvider>
          </SubscriptionAccessConfigProvider>
        </SessionProvider>
      </QueryWrapper>
    </ErrorBoundary>
  );
  // END RENDER
}

export default App;
